import React, { useContext, useEffect, useRef, useState } from 'react'
import SidebarContext from '../sidebarcontext/SidebarContext';
import { ToastContainer, toast } from 'react-toastify';
import AcademicHeader from '../AcademicHeader';
import TeacherContext from '../contextfolder/TeacherContext/TeacherContext';
import { Fetchdata } from '../hooks/getData';
import DataTable from 'react-data-table-component';
import Spinner from '../loading/spinner';

export default function ClassTeacher() {
    const { AcademicYear, todayDate, customStylesForImage, appURL, userinfo } = useContext(SidebarContext);
    const { loading, teacherList } = useContext(TeacherContext);

    const [classTeacherList, setClassTeacherList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading1, setLoading1] = useState(true);
    const [selectedTeachers, setSelectedTeachers] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const classTeachList = () => {
            const params = {
                SchID: userinfo.SchID,
                UserID: userinfo.UserID,
                Flag: "S",
                Type: "POST",
                Academicyear: userinfo.AcademicYear,
                FetchURL: `${appURL}api/admin/class-teacher`,
            };

            Fetchdata(params).then(function (result) {
                if (result.StatusCode === 200) {
                    const postResult = result.MarksInputLst ? result.MarksInputLst : [];
                    setLoading1(false);
                    setClassTeacherList(postResult);
                    setFilteredList(postResult);

                    const initialSelections = {};
                    postResult.forEach(item => {
                        const key = `${item.ClassID}_${item.SectionID}`;
                        initialSelections[key] = item.TeacherID || '';
                    });
                    setSelectedTeachers(initialSelections);
                } else {
                    setLoading1(false);
                    setClassTeacherList([]);
                    setFilteredList([]);
                }
            });
        };
        classTeachList();
    }, []);

    // Get available teachers for a specific class-section (excludes already assigned teachers)
    const getAvailableTeachers = (currentClassId, currentSectionId, currentTeacherId) => {
        if (!teacherList) return [];

        // Get all currently assigned teachers (excluding current selection)
        const assignedTeacherIds = new Set();

        Object.keys(selectedTeachers).forEach(key => {
            const [classId, sectionId] = key.split('_');
            const teacherId = selectedTeachers[key];

            // Skip the current class-section being edited
            if (parseInt(classId) === currentClassId && parseInt(sectionId) === currentSectionId) {
                return;
            }

            if (teacherId && teacherId !== '') {
                assignedTeacherIds.add(teacherId.toString());
            }
        });

        // Filter teachers - exclude those already assigned elsewhere
        return teacherList.filter(teacher => {
            // Always include currently assigned teacher for this row
            if (currentTeacherId && teacher.TeacherID === parseInt(currentTeacherId)) {
                return true;
            }

            // Exclude teachers already assigned to other class-sections
            if (assignedTeacherIds.has(teacher.TeacherID.toString())) {
                return false;
            }

            return true;
        });
    };

    const handleTeacherChange = (classId, sectionId, teacherId) => {
        const key = `${classId}_${sectionId}`;
        const originalTeacher = classTeacherList.find(
            item => item.ClassID === classId && item.SectionID === sectionId
        )?.TeacherID || '';

        setSelectedTeachers(prev => ({
            ...prev,
            [key]: teacherId
        }));

        // Check if this change is different from original
        const isChanged = teacherId !== originalTeacher;

        if (isChanged) {
            setHasChanges(true);
        } else {
            const hasAnyChanges = Object.keys(selectedTeachers).some(k => {
                const original = classTeacherList.find(
                    item => `${item.ClassID}_${item.SectionID}` === k
                )?.TeacherID || '';
                const current = k === key ? teacherId : selectedTeachers[k];
                return current !== original;
            });
            setHasChanges(hasAnyChanges);
        }
    };

    const handleSubmitAll = async (e) => {
        e.preventDefault();

        // Prepare values array with all changes
        const values = [];

        Object.keys(selectedTeachers).forEach(key => {
            const [classId, sectionId] = key.split('_');
            const teacherId = selectedTeachers[key];

            const originalItem = classTeacherList.find(
                item => item.ClassID === parseInt(classId) && item.SectionID === parseInt(sectionId)
            );

            const originalTeacherId = originalItem?.TeacherID || '';

            if (teacherId !== originalTeacherId) {
                const valueObj = {
                    ClassID: parseInt(classId),
                    SectionID: parseInt(sectionId),
                };

                if (teacherId && teacherId !== '') {
                    valueObj.TeacherID = parseInt(teacherId);
                } else {
                    valueObj.TeacherID = 0;
                }

                values.push(valueObj);
            }
        });

        if (values.length === 0) {
            toast.info("No changes to submit");
            return;
        }

        setSubmitting(true);

        const params = {
            SchID: userinfo.SchID,
            UserID: userinfo.UserID,
            Flag: "i",
            Type: "POST",
            Academicyear: userinfo.AcademicYear,
            FetchURL: `${appURL}api/admin/class-teacher`,
            Values: values
        };

        try {
            const result = await Fetchdata(params);
            if (result.StatusCode === 200) {
                const assignedCount = values.filter(v => v.TeacherID && v.TeacherID !== 0).length;
                const removedCount = values.filter(v => !v.TeacherID || v.TeacherID === 0).length;

                let successMessage = '';
                if (assignedCount > 0 && removedCount > 0) {
                    successMessage = `${assignedCount} teacher(s) assigned and ${removedCount} removed successfully!`;
                } else if (assignedCount > 0) {
                    successMessage = `${assignedCount} teacher(s) assigned successfully!`;
                } else if (removedCount > 0) {
                    successMessage = `${removedCount} teacher(s) removed successfully!`;
                }

                toast.success(successMessage);
                setHasChanges(false);
                await refreshList();
            } else {
                toast.error(result.Message || "Failed to assign teachers");
            }
        } catch (error) {
            console.error('Submission error:', error);
            toast.error("An error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    const refreshList = async () => {
        const params = {
            SchID: userinfo.SchID,
            UserID: userinfo.UserID,
            Flag: "S",
            Type: "POST",
            Academicyear: userinfo.AcademicYear,
            FetchURL: `${appURL}api/admin/class-teacher`,
        };

        const result = await Fetchdata(params);
        if (result.StatusCode === 200) {
            const postResult = result.MarksInputLst ? result.MarksInputLst : [];
            setClassTeacherList(postResult);
            setFilteredList(postResult);

            const initialSelections = {};
            postResult.forEach(item => {
                const key = `${item.ClassID}_${item.SectionID}`;
                initialSelections[key] = item.TeacherID || '';
            });
            setSelectedTeachers(initialSelections);
        }
    };

    const resetChanges = () => {
        const originalSelections = {};
        classTeacherList.forEach(item => {
            const key = `${item.ClassID}_${item.SectionID}`;
            originalSelections[key] = item.TeacherID || '';
        });
        setSelectedTeachers(originalSelections);
        setHasChanges(false);
        toast.info("Changes reset");
    };

    const getPendingChangesCount = () => {
        return Object.keys(selectedTeachers).filter(key => {
            const [classId, sectionId] = key.split('_');
            const original = classTeacherList.find(
                item => item.ClassID === parseInt(classId) && item.SectionID === parseInt(sectionId)
            );
            const originalTeacherId = original?.TeacherID || '';
            const currentTeacherId = selectedTeachers[key] || '';
            return currentTeacherId !== originalTeacherId;
        }).length;
    };

    const searchInput = useRef("");

    const searchHandler = (e) => {
        const srchQuery = e.target.value.toLowerCase();
        if (srchQuery) {
            let srchResult = classTeacherList.filter((list) => {
                return list.Teacher?.toLowerCase().includes(srchQuery) ||
                    list.Class?.toLowerCase().includes(srchQuery) ||
                    list.Section?.toLowerCase().includes(srchQuery);
            });
            setFilteredList(srchResult);
        } else {
            setFilteredList(classTeacherList);
        }
    };

    const columns = [
        {
            name: "S.N.",
            width: "63px",
            center: true,
            cell: (row, index) => index + 1,
        },
        {
            name: "Class",
            selector: (row) => row.Class,
            sortable: true,
        },
        {
            name: "Section",
            selector: (row) => row.Section,
            sortable: true,
        },
        {
            name: "Current Class Teacher",
            selector: (row) => row.Teacher || "Not Assigned",
            sortable: true,
        },
        {
            name: "Assign/Update Teacher",
            cell: (row) => {
                const key = `${row.ClassID}_${row.SectionID}`;
                const currentValue = selectedTeachers[key] || '';
                const originalValue = row.TeacherID || '';
                const isChanged = currentValue !== originalValue;
                const availableTeachers = getAvailableTeachers(row.ClassID, row.SectionID, currentValue);

                return (
                    <select
                        className={`form-select form-select-sm ${isChanged ? 'border-warning bg-warning bg-opacity-10' : ''}`}
                        aria-label="Select teacher"
                        value={currentValue}
                        onChange={(e) => handleTeacherChange(row.ClassID, row.SectionID, e.target.value)}
                        style={{ width: "220px" }}
                    >
                        <option value="">-- Select Teacher --</option>
                        {availableTeachers && availableTeachers.length > 0 ? (
                            availableTeachers.map((list) => {
                                return (
                                    <option key={list.TeacherID} value={list.TeacherID}>
                                        {list.TeacherName}
                                    </option>
                                );
                            })
                        ) : (
                            <option disabled>No teachers available</option>
                        )}
                    </select>
                );
            },
        },
    ];

    if (loading1) {
        return <Spinner />;
    }

    const pendingCount = getPendingChangesCount();

    return (
        <>
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
            />

            <div className="col-lg-12 col-md-12 col-sm-12 contentMainSection">
                <div className="row">
                    <div className="page-header">
                        <div className="text-start page-title">Class Teacher</div>
                        <div className="page-date">
                            <div className="sec-content">
                                <AcademicHeader
                                    todayDate={todayDate}
                                    AcademicYear={AcademicYear}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <hr />
                <div className="sec-dataTable content-section main-content">
                    <div className="upper-dataTbl d-flex justify-content-start align-items-center gap-2 mb-3">
                        {/* <div>
                            {hasChanges && (
                                <span className="badge bg-warning text-dark">
                                    {pendingCount} pending change(s)
                                </span>
                            )}
                        </div> */}
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={resetChanges}
                                disabled={submitting || !hasChanges}
                            >
                                <i className="bi bi-arrow-repeat me-1"></i>
                                Reset Changes
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={handleSubmitAll}
                                disabled={submitting || !hasChanges}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-all me-1"></i>
                                        Submit All {pendingCount > 0 && `(${pendingCount})`}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    <hr className="new-hr" />

                    <DataTable
                        columns={columns}
                        data={filteredList}
                        customStyles={customStylesForImage}
                        pagination
                        fixedHeader
                        fixedHeaderScrollHeight="280px"
                        highlightOnHover
                        pointerOnHover
                        responsive
                        dense
                        striped
                        subHeader
                        subHeaderAlign="right"
                        subHeaderComponent={
                            <div className="col-lg-3 col-md-4 col-sm-12">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        placeholder="Search by class, section or teacher..."
                                        ref={searchInput}
                                        type="text"
                                        className="form-control"
                                        onChange={searchHandler}
                                    />
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        </>
    )
}